/**
 * Hooks the TSL post pipeline (src/tsl/post.ts) into R3F's render job.
 * setRenderOverride swaps renderer.render(scene, camera) for pipeline.render()
 * while R3F keeps its frame loop, fps throttle and error handling.
 *
 * Adaptive quality (src/quality): MSAA and bloom on/off need a pipeline rebuild (new
 * render-target sample count, different node graph); bloom resolution is live.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react'
import { setRenderOverride, useStore, useThree } from '@react-three/fiber/webgpu'
import { createPostPipeline, defaultPostSettings, type PostPipeline } from '../tsl/post'
import { flags } from '../config/flags'
import { perf } from '../debug/perf'
import { qualityStore } from '../quality/store'

/** Grain cell in physical pixels: 1 at dpr 1, 2 at dpr 2–3, so it reads the same size everywhere. */
const grainSizeForDpr = (dpr: number) => Math.max(1, Math.round(dpr * 0.75))

export function PostFX() {
  const store = useStore()
  const renderer = useThree((s) => s.renderer)
  const scene = useThree((s) => s.scene)
  const camera = useThree((s) => s.camera)
  const dpr = useThree((s) => s.viewport.dpr)
  const quality = useSyncExternalStore(qualityStore.subscribe, qualityStore.get)
  const fxRef = useRef<PostPipeline | null>(null)

  const bloomOn = quality.bloom && flags.bloom > 0

  useEffect(() => {
    if (!flags.post) {
      perf.post = 'off (?post=0)'
      return
    }
    const fx = createPostPipeline(renderer, scene, camera, {
      ...defaultPostSettings,
      bloom: bloomOn ? flags.bloom : 0,
      bloomSource: flags.bloomSource,
      bloomRadius: flags.bloomRadius,
      grain: flags.grain,
      grainSize: grainSizeForDpr(store.getState().viewport.dpr),
      msaa: quality.msaa,
      bloomResolution: qualityStore.get().bloomRes,
    })
    fxRef.current = fx
    perf.post = fx.describe()
    setRenderOverride(store, () => fx.render())
    return () => {
      setRenderOverride(store, null)
      fxRef.current = null
      fx.dispose()
    }
    // bloomRes is deliberately not a dependency: it's applied live below.
  }, [store, renderer, scene, camera, quality.msaa, bloomOn])

  useEffect(() => {
    const fx = fxRef.current
    if (!fx) return
    fx.setBloomResolution(quality.bloomRes)
    perf.post = fx.describe()
  }, [quality.bloomRes])

  // dpr can change at runtime (zoom, moving the window between screens, render scale): uniform update, no rebuild.
  useEffect(() => {
    const u = fxRef.current?.uniforms.grainSize
    if (u) u.value = grainSizeForDpr(dpr)
  }, [dpr])

  return null
}
